import { forwardRef, ReactNode } from "react";
import { Box, IconButton, Toolbar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DrawerContentWrapper, DrawerHeader } from "../../../shared/ui/Drawer";

/**
 * Props for the NavigationContent component.
 * @property isMobile - Whether the drawer is in mobile mode.
 * @property onDrawerToggle - Callback to toggle the drawer open/closed.
 * @property children - Drawer content.
 */
export interface NavigationContentProps {
    isMobile: boolean;
    onDrawerToggle: () => void;
    children?: ReactNode;
}

/**
 * NavigationContent
 *
 * Drawer content wrapper for navigation. Handles mobile/desktop header and close button.
 * Forwards ref to the drawer content wrapper for measurement and focus management.
 */
export const NavigationContent = forwardRef<
    HTMLDivElement,
    NavigationContentProps
>(({ isMobile, onDrawerToggle, children }, ref) => {
    const theme = useTheme();

    return (
        <DrawerContentWrapper ref={ref}>
            {/* Mobile header with close button */}
            {isMobile && (
                <>
                    <DrawerHeader>
                        <IconButton onClick={onDrawerToggle}>
                            {theme.direction === "ltr" ? (
                                <ChevronLeft />
                            ) : (
                                <ChevronRight />
                            )}
                        </IconButton>
                    </DrawerHeader>
                </>
            )}

            {/* Desktop toolbar spacer */}
            {!isMobile && <Toolbar />}

            {/* Content */}
            <Box sx={{ overflow: "auto" }}>{children}</Box>
        </DrawerContentWrapper>
    );
});
